/**
 * @fileoverview Pivot table renderer (MOR-906 §7.8)
 *
 * Renders a `runPivot` result as the table a remuneration report actually
 * contains: row dimensions down the left, pinned row measures (Rate after
 * the person) beside them, the column dimension across the top, and one
 * sub-column per remaining measure beneath each. Headers are draggable so
 * the layout can be rearranged the way a practitioner would in Excel.
 *
 * Dragging does not slide `<th>` cells — table layout fights CSS transforms
 * and looks broken. The source stays put, a label chip follows the pointer,
 * and the drop target highlights until release. The grab cursor is the
 * only hover affordance — no icon on the header copy.
 *
 * @component
 * @example
 * <PivotTable result={result} title="Remuneration table" spec={spec} onSpecChange={patch} />
 */

import { useMemo, useState, type ReactNode } from "react";
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  closestCenter,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";

import type { TimePivotSpec } from "@cvx/timesheets/savedPivotReports/schema";

import { cn } from "@/utils/misc";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { formatAudExact, formatDateRange } from "./format";
import {
  applyPivotDrag,
  buildLeadingColumns,
  formatLeading,
  formatMeasure,
  leadingValue,
  MEASURE_LABELS,
  parsePivotDragId,
  pivotDragId,
  type LeadingColumn,
  type PivotResult,
} from "./pivotExport";

/** Stands in for the row-total group in the measure header row. */
const TOTAL_GROUP = "\u0000total";

/**
 * Prefer the header under the pointer so Rate-onto-Person is exact; fall
 * back to closest-center when the pointer is between two heads.
 */
const headerCollision: CollisionDetection = (args) => {
  const underPointer = pointerWithin(args);
  return underPointer.length > 0 ? underPointer : closestCenter(args);
};

function DraggableHead({
  id,
  disabled,
  className,
  rowSpan,
  colSpan,
  align = "left",
  children,
}: {
  id: string;
  disabled?: boolean;
  className?: string;
  rowSpan?: number;
  colSpan?: number;
  align?: "left" | "center" | "right";
  children: ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({ id, disabled });
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id, disabled });

  return (
    <TableHead
      ref={(node) => {
        setDragRef(node);
        setDropRef(node);
      }}
      rowSpan={rowSpan}
      colSpan={colSpan}
      className={cn(
        className,
        !disabled && "cursor-grab select-none active:cursor-grabbing",
        isDragging && "opacity-40",
        isOver && !isDragging && "bg-primary/10 ring-1 ring-inset ring-primary",
      )}
    >
      {disabled ? (
        children
      ) : (
        <button
          type="button"
          className={cn(
            "inline-flex w-full touch-none items-center text-inherit",
            "cursor-grab active:cursor-grabbing",
            align === "center" && "justify-center",
            align === "right" && "justify-end",
          )}
          title="Drag to move this column"
          {...attributes}
          {...listeners}
        >
          {children}
        </button>
      )}
    </TableHead>
  );
}

export function PivotTable({
  result,
  title,
  hideTitle,
  spec,
  onSpecChange,
}: {
  result: PivotResult;
  /** Shown above the table and used as the export filename. */
  title: string;
  /** Set where the surrounding card already names the table. */
  hideTitle?: boolean;
  /** When both are set, headers can be dragged to rearrange the layout. */
  spec?: TimePivotSpec;
  onSpecChange?: (patch: Partial<TimePivotSpec>) => void;
}) {
  const sortable = spec !== undefined && onSpecChange !== undefined;
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 180, tolerance: 6 },
    }),
  );

  const leading = useMemo(
    () =>
      buildLeadingColumns(
        result.rowDimensions,
        result.rowHeaders,
        result.rowMeasures,
        result.rowMeasureAfter,
      ),
    [
      result.rowDimensions,
      result.rowHeaders,
      result.rowMeasures,
      result.rowMeasureAfter,
    ],
  );

  const hasColumns = result.columns.length > 0;
  const measureCount = result.measures.length;
  const columnGroups = useMemo(
    () => (hasColumns ? [...result.columns, TOTAL_GROUP] : [TOTAL_GROUP]),
    [hasColumns, result.columns],
  );
  const showCellGroups = measureCount > 0;

  const labelById = useMemo(() => {
    const labels = new Map<string, string>();
    for (const col of leading) {
      labels.set(leadingId(col), col.label);
    }
    for (const column of result.columns) {
      labels.set(pivotDragId({ type: "col", key: column }), column);
    }
    for (const group of columnGroups) {
      for (const measure of result.measures) {
        labels.set(
          `${pivotDragId({ type: "cellMeasure", key: measure })}#${group}`,
          MEASURE_LABELS[measure],
        );
      }
    }
    return labels;
  }, [leading, result.columns, result.measures, columnGroups]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    if (!spec || !onSpecChange || !event.over) return;
    const active = parsePivotDragId(String(event.active.id));
    const over = parsePivotDragId(String(event.over.id));
    if (!active || !over) return;
    const patch = applyPivotDrag(spec, result.columns, active, over);
    if (patch) onSpecChange(patch);
  };

  if (result.rows.length === 0) {
    return (
      <div className="flex h-40 flex-col items-center justify-center gap-1 text-sm text-muted-foreground">
        <p className="font-medium text-foreground">Nothing to show</p>
        <p className="text-xs">
          No time matched this report over{" "}
          {formatDateRange(result.startDate, result.endDate)}.
        </p>
      </div>
    );
  }

  const table = (
    <Table>
      <TableHeader>
        <TableRow>
          {leading.map((col) => (
            <DraggableHead
              key={leadingId(col)}
              id={leadingId(col)}
              disabled={!sortable}
              rowSpan={2}
              className="align-bottom"
            >
              {col.label}
            </DraggableHead>
          ))}
          {showCellGroups &&
            hasColumns &&
            result.columns.map((column) => (
              <DraggableHead
                key={column}
                id={pivotDragId({ type: "col", key: column })}
                disabled={!sortable}
                colSpan={measureCount}
                align="center"
                className="border-l border-border/50 text-center"
              >
                {column}
              </DraggableHead>
            ))}
          {showCellGroups && (
            <TableHead
              colSpan={measureCount}
              className="border-l border-border/50 text-center"
            >
              Total
            </TableHead>
          )}
        </TableRow>
        {showCellGroups && (
          <TableRow>
            {columnGroups.map((group) =>
              result.measures.map((measure, i) => (
                <DraggableHead
                  key={`${group}-${measure}`}
                  id={`${pivotDragId({ type: "cellMeasure", key: measure })}#${group}`}
                  disabled={!sortable}
                  align="right"
                  className={cn(
                    "text-right text-xs font-normal",
                    i === 0 && "border-l border-border/50",
                  )}
                >
                  {MEASURE_LABELS[measure]}
                </DraggableHead>
              )),
            )}
          </TableRow>
        )}
      </TableHeader>
      <TableBody>
        {result.rows.map((row, rowIndex) => (
          <TableRow
            key={`${row.path.join("/")}-${row.kind}-${rowIndex}`}
            className={cn(
              row.kind === "subtotal" &&
                "bg-muted/40 font-medium hover:bg-muted/40",
            )}
          >
            {leading.map((col) => (
              <TableCell
                key={leadingId(col)}
                className={cn(
                  "text-sm",
                  col.kind === "measure" && "text-right tabular-nums",
                )}
              >
                {formatLeading(
                  col,
                  leadingValue(
                    col,
                    row.path,
                    result.rowDimensions,
                    row.rowValues,
                    result.rowMeasures,
                  ),
                )}
              </TableCell>
            ))}
            {showCellGroups &&
              [...row.cells, row.total].map((cell, cellIndex) =>
                cell.values.map((value, i) => (
                  <TableCell
                    key={`${cellIndex}-${i}`}
                    className={cn(
                      "text-right text-sm tabular-nums",
                      i === 0 && "border-l border-border/50",
                      cellIndex === row.cells.length && "font-medium",
                    )}
                  >
                    {formatMeasure(result.measures[i] ?? "hours", value)}
                  </TableCell>
                )),
              )}
          </TableRow>
        ))}
        <TableRow className="bg-muted/60 hover:bg-muted/60">
          {leading.map((col, i) => (
            <TableCell
              key={leadingId(col)}
              className={cn(
                "text-sm font-semibold",
                col.kind === "measure" && "text-right tabular-nums",
              )}
            >
              {col.kind === "row"
                ? i === 0
                  ? "Total"
                  : ""
                : formatLeading(
                    col,
                    result.rowGrandTotal[result.rowMeasures.indexOf(col.key)] ??
                      0,
                  )}
            </TableCell>
          ))}
          {showCellGroups &&
            [...result.columnTotals, result.grandTotal].map((cell, cellIndex) =>
              cell.values.map((value, i) => (
                <TableCell
                  key={`${cellIndex}-${i}`}
                  className={cn(
                    "text-right text-sm font-semibold tabular-nums",
                    i === 0 && "border-l border-border/50",
                  )}
                >
                  {formatMeasure(result.measures[i] ?? "hours", value)}
                </TableCell>
              )),
            )}
        </TableRow>
      </TableBody>
    </Table>
  );

  const body = (
    <>
      <div className="overflow-x-auto">{table}</div>
      {sortable && (
        <p className="text-xs text-muted-foreground">
          Drag header to reorder columns.
        </p>
      )}
    </>
  );

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        {!hideTitle && <p className="text-sm font-semibold">{title}</p>}
        <p className="text-xs text-muted-foreground">
          {formatDateRange(result.startDate, result.endDate)}
          {result.note ? ` · ${result.note}` : ""}
          {result.truncated && " · large range truncated"}
        </p>
      </div>

      {sortable ? (
        <DndContext
          sensors={sensors}
          collisionDetection={headerCollision}
          autoScroll={false}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveId(null)}
        >
          {body}
          <DragOverlay dropAnimation={{ duration: 160, easing: "ease-out" }}>
            {activeId ? (
              <div className="cursor-grabbing rounded-md border border-border bg-background px-2.5 py-1.5 text-sm font-medium shadow-lg">
                {labelById.get(activeId) ?? "Column"}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        body
      )}

      {result.gst !== undefined && (
        <div className="flex flex-col items-end gap-0.5 pr-2 text-sm">
          <span className="text-muted-foreground">
            GST (10%){" "}
            <span className="font-medium tabular-nums text-foreground">
              {formatAudExact(result.gst)}
            </span>
          </span>
          {result.totalIncGst !== undefined && (
            <span className="font-semibold tabular-nums">
              Total incl. GST {formatAudExact(result.totalIncGst)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function leadingId(col: LeadingColumn): string {
  return col.kind === "row"
    ? pivotDragId({ type: "row", key: col.key })
    : pivotDragId({ type: "rowMeasure", key: col.key });
}
